import { useState } from 'react';
import { Drawer, DrawerContent, DrawerClose } from '@components/ui/Drawer';
import Modal from '@components/ui/Modal';
import { RiCloseLine, RiAlertFill, RiDownloadLine, RiLoader4Line, RiBankCardLine, RiBankLine, RiCloseCircleFill, RiErrorWarningLine } from '@remixicon/react';
import { formatUTCToDateTime, trunc2 } from '@/utils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { apiClient } from '@/lib/api';
import { buildInvoicePaymentSecuredBookingEmailHtml, buildInvoicePaymentHoldEmailHtml } from '@/utils/emailTemplates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@store/authStore';
import Swal from 'sweetalert2';
import { getQuoteStatusConfig } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { ROLES, normalizeRole } from '@/lib/permissions';
import { usePlatformFeeRate } from '@/hooks/usePlatformFeeRate';
import { formatPlatformFeePercent } from '@/utils/platformFee';
import { storageKeys } from '@/lib/storage';
import PaymentSettlementNoticeModal, { isSettlementNoticeDismissed } from './PaymentSettlementNoticeModal';

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Format a user record's first/last name into a single display string.
 * Returns "—" when the record is missing or has no name so an invoice
 * whose sender hasn't been captured yet still renders a neutral row.
 */
const formatUserFullName = (user) => {
  if (!user) return '—';
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.email || '—';
};

const AdvanceInvoiceDrawer = ({ isOpen, onClose, invoice: invoiceProp, invoiceId: invoiceIdProp, status, paymentStatus }) => {
  
  const queryClient = useQueryClient();
  const { userProfile } = useAuthStore();
  // Admin-only fields (Created By / Sent By) at the bottom of the drawer.
  // super_admin sees admin-only surfaces by convention here.
  const { role } = usePermissions();
  const normalizedRole = normalizeRole(role);
  const isAdminRole =
    normalizedRole === ROLES.ADMIN || normalizedRole === ROLES.SUPER_ADMIN;
  const [isGenerating, setIsGenerating] = useState(false);
  const [authActionLoading, setAuthActionLoading] = useState(null); // 'confirmCapture' | 'confirmOnly' | 'reject'
  // Payment Settlement Notice — one-time informational modal shown before
  // capturing card funds. Skipped when the user has previously opted out
  // via the "Don't show again" checkbox (persisted in localStorage).
  const [settlementNoticeOpen, setSettlementNoticeOpen] = useState(false);
  // Card capture failure — the toast shows the human summary; the modal
  // holds the raw provider error, revealed only when the broker clicks
  // "Learn more" from the toast.
  const [captureErrorOpen, setCaptureErrorOpen] = useState(false);
  const [captureErrorDetails, setCaptureErrorDetails] = useState('');

  const openConfirmAndCaptureFlow = () => {
    // The settlement disclaimer only applies to pre-auth card captures —
    // that's when the 1–3 day bank-settlement window (and its decline
    // risk) is actually in play. Any other payment state skips the notice
    // and goes straight to the action. Broker's per-user opt-out still
    // wins.
    const dismissed = isSettlementNoticeDismissed(
      storageKeys.INVOICE_SETTLEMENT_NOTICE_DISMISSED,
      userProfile?.userprofile_id,
    );
    if (dismissed) {
      handleConfirmAndCapture();
      return;
    }
    setSettlementNoticeOpen(true);
  };

  // When invoiceIdProp is provided, refetch the invoice fresh (with related
  // items, client, quote, payments) instead of trusting a potentially stale
  // invoice object passed from a list/report.
  const { data: fetchedInvoice } = useQuery({
    queryKey: ['invoice', invoiceIdProp],
    queryFn: () => apiClient.getInvoiceById(invoiceIdProp, null, { include: 'items,client,quote,payments,creator,sender' }),
    enabled: !!invoiceIdProp,
  });

  const invoice = fetchedInvoice || invoiceProp;

  const { resolveForSubtotal } = usePlatformFeeRate();
  const { percent: feePercentEffective, rate: feeRateEffective } = resolveForSubtotal(invoice?.subtotal);

  const payments = (invoice?.payments || []).filter(p => p.invoice_id === invoice?.invoice_id).at(-1) || {};

  // 7-day pre-auth window (Visa/MC standard). Past that window the funds
  // are released automatically and the sale-completion call will fail.
  const holdAnchor = invoice?.created_at || payments?.created_at;
  const holdExpiresAt = holdAnchor
    ? new Date(new Date(holdAnchor).getTime() + 7 * 24 * 60 * 60 * 1000)
    : null;
  const isHoldExpired = holdExpiresAt && Date.now() > holdExpiresAt.getTime();

  const { data: organization } = useQuery({
    queryKey: ['organization', invoice?.broker_org_id],
    queryFn: () => apiClient.getOrganizationById(invoice.broker_org_id),
    enabled: !!invoice?.broker_org_id,
  });

  const { data: brokerProfile } = useQuery({
      queryKey: ['broker-profile', invoice?.created_by],
      // invoice.created_by holds a userprofile_id — hit /users/{userprofile_id}
      // (getUser), NOT the by-user-id variant which resolves a Supabase
      // auth user id and 404s on userprofile ids.
      queryFn: () => apiClient.getUser(invoice.created_by),
      enabled: !!invoice?.created_by
    });

  if (!invoice) return null;

  const configStatus = getQuoteStatusConfig(status=="PAID" ? "ACCEPTED" : status);
  const Icon = configStatus.icon;
  const invoiceId = invoiceIdProp || invoice.invoice_id;
  const clientName = [invoice.client?.first_name, invoice.client?.last_name].filter(Boolean).join(' ') || '—';
  const isPaid = invoice.status == 'PAID';
  const items = invoice.items?.length > 0
    ? invoice.items
    : (invoice.files?.services || []).map(s => ({ item: s.item, amount: s.amount, tax_amount: 0 }));
  const total = invoice.total_amount || 0;
  const sentOn = invoice.created_at
    ? formatUTCToDateTime(invoice.created_at, 'UTC', 'D MMM YYYY, HH:mm')
    : '—';

  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    try {
      const container = document.createElement('div');
      container.style.cssText = `
        position: fixed; top: -9999px; left: -9999px;
        width: 560px; background: #ffffff; padding: 32px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      `;
      container.innerHTML = `
        <div style="border:1px solid #fed7aa;border-radius:20px;overflow:hidden;padding-bottom:8px;">
          <p style="font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#9ca3af;padding:12px 16px;margin:0;">Advanced Invoice</p>
          ${!isPaid ? `<div style="margin:0 12px 12px;display:flex;align-items:center;gap:8px;padding:0px 12px;background:#FFF3EB;border-radius:8px;">
            <span style="font-size:12px;color:#FA7319;">⚠</span><span style="font-size:12px;color:#374151;">Unpaid advanced invoice</span></div>` : ''}
          <div style="padding:0 16px;">
            ${items.map(item => `
            <div style="display:flex;justify-content:space-between;padding:6px 0 2px;">
              <span style="font-size:13px;color:#4b5563;">Item Cost</span>
              <span style="font-size:13px;color:#111827;font-weight:500;">$${fmt(item.amount)}</span>
            </div>
            ${item.tax_amount > 0 ? `<div style="display:flex;justify-content:space-between;padding:2px 0 4px;">
              <span style="font-size:11px;color:#9ca3af;">Taxes (10%)</span>
              <span style="font-size:11px;color:#6b7280;">$${fmt(item.tax_amount)}</span>
            </div>` : ''}`).join('')}
            <div style="border-top:1px solid #f3f4f6;margin-top:4px;padding-top:4px;">
              <div style="display:flex;justify-content:space-between;padding:8px 0 4px;">
                <span style="font-size:14px;color:#111827;font-weight:500;">Total Invoice Cost</span>
                <span style="font-size:14px;color:#111827;font-weight:500;">$${fmt(total)}</span>
              </div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;">
                <span style="font-size:11px;color:#9ca3af;">Sent on</span>
                <span style="font-size:11px;color:#9ca3af;">${sentOn}</span>
              </div>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(container);
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const margin = 40;
      const imgWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
      pdf.save(`invoice-${invoice.invoice_number || 'download'}.pdf`);
    } finally {
      setIsGenerating(false);
    }
  };


  const handleConfirmAndCapture = async () => {
      if (authActionLoading || !invoiceId) return;
      if (isHoldExpired) {
        toast.error('Payment hold has expired. Re-authorization is required before this invoice can be captured.');
        return;
      }
      setAuthActionLoading('confirmCapture');
      try {

        // capture sales
        const sales = await apiClient.saleCompletionMxMerchantPayment({ invoice_id: invoiceId });
   
        if(sales?.status=="CAPTURED")
        {
          await apiClient.updateInvoice(invoiceId, { status: 'PAID' });
        }

        // Send payment confirmation email to client
        if (invoice?.client?.email) {
          try {
            const emailHtml = buildInvoicePaymentSecuredBookingEmailHtml({
              invoice: invoice,
              sales,
              organization
            });

            apiClient.sendMessage({
              From: 'tools@trustjet.ai',
              To: invoice?.client?.email,
              ReplyTo: brokerProfile?.email || '',
              Subject: `TrustJet – Booking Secured (${invoice?.quote_number || invoice?.invoice_number})`,
              HtmlBody: emailHtml
            })
              .then(() => console.log('Payment confirmation email sent to', invoice?.client?.email))
              .catch((err) => console.error('Failed to send payment confirmation email:', err));
          } catch (emailErr) {
            console.error('Failed to build payment confirmation email:', emailErr);
          }
        }
  
        //invalidates quotes
        queryClient.invalidateQueries({ queryKey: ['quoteDetails', invoiceId] });
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        queryClient.invalidateQueries({ queryKey: ['payments'] });
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });

        //success toast message
        toast.success('Invoice confirmed and payment is being processed.');
  
        // close drawer
        onClose?.();
      } catch (e) {
        console.error('Failed to confirm & capture:', e);
        const tripNumber = invoice?.quote?.quote_number || invoice?.invoice_number || invoiceId;
        setCaptureErrorDetails(e?.message || 'Unknown error');
        toast.error(
          <div className="flex items-start gap-2">
            <RiCloseCircleFill className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="flex flex-col items-start gap-1">
              <span>Card payment for <span className="font-semibold">{tripNumber}</span> failed to clear.</span>
              <button
                type="button"
                onClick={() => setCaptureErrorOpen(true)}
                className="cursor-pointer text-red-600 underline underline-offset-2 hover:text-red-700 text-sm"
              >
                Learn more
              </button>
            </div>
          </div>,
          { duration: 10000, icon: null },
        );
      } finally {
        setAuthActionLoading(null);
      }
    };

     const handleConfirmWithoutCapture = async () => {
        if (authActionLoading || !invoiceId) return;

        setAuthActionLoading('confirmOnly');
        try {

          await apiClient.updateInvoice(invoiceId, { status: 'PAID', payment_status: 'CAPTURED' });

          queryClient.invalidateQueries({ queryKey: ['quoteDetails', invoiceId] });
          queryClient.invalidateQueries({ queryKey: ['quotes'] });
          queryClient.invalidateQueries({ queryKey: ['invoices'] });
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });


          // Send payment confirmation email to client
        if (invoice?.client?.email) {
          try {

            const sales = {
              withOutSales: true
            };

            const emailHtml = buildInvoicePaymentSecuredBookingEmailHtml({
              invoice,
              sales,
              organization
            });
  
            apiClient.sendMessage({
              From: 'tools@trustjet.ai',
              To: invoice?.client?.email,
              ReplyTo: brokerProfile?.email || '',
              Subject: `TrustJet – Booking Secured (${invoice?.quote_number || invoice?.invoice_number})`,
              HtmlBody: emailHtml
            })
              .then(() => console.log('Payment confirmation email sent to', invoice?.client?.email))
              .catch((err) => console.error('Failed to send payment confirmation email:', err));
          } catch (emailErr) {
            console.error('Failed to build payment confirmation email:', emailErr);
          }
        }
    
          // show success toast message
          toast.success('Invoice confirmed and card hold has been released.');
    
          // close drawer
          onClose?.();
        } catch (e) {
          console.error('Failed to confirm without capture:', e);
          toast.error('Failed to confirm trip');
        } finally {
          setAuthActionLoading(null);
        }
      };
  
    const handleRejectAndRelease = async () => {
      if (authActionLoading || !invoiceId) return;

      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Reject this invoice?',
        html: 'This will <strong>cancel the invoice</strong> and <strong>release the authorized payment hold</strong> on the client\'s card. This can\'t be undone.',
        showCancelButton: true,
        confirmButtonText: 'Yes, reject invoice',
        cancelButtonText: 'Keep invoice',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#000a1f',
        reverseButtons: true,
        focusCancel: true,
        // The drawer runs Radix Dialog in modal mode, which raises a stacking
        // context AND disables pointer events on siblings — so by default the
        // Swal renders behind the drawer and can't be clicked. Force it above
        // and re-enable pointer events on its container.
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) {
            container.style.setProperty('z-index', '9999', 'important');
            container.style.setProperty('pointer-events', 'auto', 'important');
          }
        },
      });
      if (!isConfirmed) return;

      await apiClient.updateInvoice(invoiceId, { status: 'VOID', payment_status: 'CANCELLED' });

      // Send payment confirmation email to client
      if (invoice?.client?.email) {
        try {
          const emailHtml = buildInvoicePaymentHoldEmailHtml({
            clientName: `${invoice?.client?.first_name} ${invoice?.client?.last_name}`,
            itemCost: invoice?.subtotal || 0,
            total: invoice?.total_amount || 0,
            quoteNumber: invoice?.quote_number || "",
            trustjetFee: invoice?.platform_fee || 0,
            taxAmount: invoice?.tax_amount || 0,
            services: invoice?.items || [],
            organization,
            cardFee: invoice?.card_fee || 0,
            last4: payments?.related_transactions?.pre_authorization?.cardAccount?.last4 || "",
          });

          apiClient.sendMessage({
            From: 'tools@trustjet.ai',
            To: invoice?.client?.email,
            ReplyTo: brokerProfile?.email || '',
            Subject: `TrustJet – Payment Hold Released (${invoiceId})`,
            HtmlBody: emailHtml
          })
            .then(() => console.log('Payment confirmation email sent to', invoice?.client?.email))
            .catch((err) => console.error('Failed to send payment confirmation email:', err));
        } catch (emailErr) {
          console.error('Failed to build payment hold released confirmation email:', emailErr);
        }
      }
  
      setAuthActionLoading('reject');
  
      try {
  
        // update quote to cancelled
        await apiClient.updateInvoice(invoiceId, { status: 'VOID' });
  
        queryClient.invalidateQueries({ queryKey: ['quoteDetails', invoiceId] });
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        queryClient.invalidateQueries({ queryKey: ['payments'] });
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });

        toast.success('Invoice cancelled and payment released from card.');

        // close drawer
        onClose?.();

      } catch (e) {
        console.error('Failed to reject & release:', e);
        toast.error('Failed to reject trip and release payment');
      } finally {
        setAuthActionLoading(null);
      }
    };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent
        side="right"
        hideCloseButton
        className="w-full max-w-md lg:max-w-2xl p-0 flex flex-col"
      >
        {/* Header */}
        <div className="px-5 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-2xl text-black font-medium truncate">{invoice?.quote?.quote_number || invoice?.invoice_number}</p>
              <p className="text-sm text-gray-700 mt-2">{invoice.invoice_type=="ADVANCE" ? 'Advanced' : 'Additional'}  Invoice for {clientName}</p>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <DrawerClose asChild>
                <button className="p-1 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors">
                  <RiCloseLine className="w-4 h-4" />
                </button>
              </DrawerClose>
              <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${configStatus.className}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {configStatus.label}
                </span>
            </div>
          </div>
        </div>

        {/* Payment hold expiry — 7-day pre-auth window (Visa/MC).
            Only show when the invoice badge itself is AUTHORIZED — a stale
            payment record on a still-SENT invoice shouldn't surface a hold
            warning. */}
        {status === 'PRE_AUTHORIZED' && payments?.status === 'PRE_AUTHORIZED' && (() => {
          const msLeft = holdExpiresAt ? holdExpiresAt.getTime() - Date.now() : null;
          const daysLeft = msLeft != null ? Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000))) : null;
          const whenStr = holdExpiresAt
            ? formatUTCToDateTime(holdExpiresAt.toISOString(), 'UTC', 'D MMM YYYY, HH:mm')
            : null;
          return (
            <div className="px-5 pt-4">
              <div className="border border-red-200 bg-red-50 rounded-xl p-3 flex items-start gap-2">
                <RiAlertFill className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />
                <p className="text-sm text-red-700">
                  {holdExpiresAt
                    ? (isHoldExpired
                        ? `Payment hold expired on ${whenStr}. Re-authorization required.`
                        : `Payment hold expires on ${whenStr} (in ${daysLeft} day${daysLeft === 1 ? '' : 's'}).`)
                    : 'Payment hold expiry unknown — invoice/payment date missing.'}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Preferred Payment Method */}
          {(() => {
            if(paymentStatus=="WIRE_TRANSFER_IN_PROGRESS" || paymentStatus=="PRE_AUTHORIZED" || paymentStatus=="PAYMENT_PROCESSING" || paymentStatus=="CAPTURED")
            {
              let isCard = false;
              if(invoice?.preferred_method)
              {
                isCard = invoice?.preferred_method == "CREDIT_CARD";
              }else{
                isCard = paymentStatus != "WIRE_TRANSFER_IN_PROGRESS";
              }
              const label = !isCard ? 'Wire Transfer' : 'Card';
              const Icon = !isCard ? RiBankLine : RiBankCardLine;
              return (
                <div className="px-5 pt-4">
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    <span className="text-sm text-gray-600">Preferred payment method</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                      <Icon className="w-4 h-4 text-gray-700" />
                      {label}
                    </span>
                  </div>
                </div>
              );
            }
          })()}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 space-y-4">
          <div className="rounded-3xl pb-2 border border-orange-200 overflow-hidden">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 px-4 py-3">
              {invoice.invoice_type=="ADVANCE" ? 'Advanced' : 'Additional'} Invoice
            </p>

            {/* Unpaid badge */}
            {!isPaid && (
              <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2 bg-amberlight rounded-lg">
                <RiAlertFill className="w-3.5 h-3.5 shrink-0 text-amberdark" />
                <span className="text-xs text-gray-600">Unpaid {invoice.invoice_type?.toLowerCase()} invoice</span>
              </div>
            )}

            {/* Cost rows */}
            <div className="px-4">
              {invoice.invoice_type=="ADVANCE" && items.map((item, i) => (
                <div key={i} className="py-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-gray-600 truncate pr-2">Item Cost</span>
                    <span className="text-[13px] text-gray-600 font-medium shrink-0">${fmt(item.amount)}</span>
                  </div>
                  {item.tax_amount > 0 && (
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[13px] text-gray-600">Taxes (10%)</span>
                      <span className="text-[13px] text-gray-600 font-medium ">${fmt(item.tax_amount)}</span>
                    </div>
                  )}
                </div>
              ))}

              {invoice.invoice_type=="ADDITIONAL" && items.map((item, i) => (
                <div key={i} className="p-2 bg-gray-50 mt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-gray-600 truncate pr-2">{item.item}</span>
                    <span className="text-[13px] text-gray-500 shrink-0">${fmt(item.amount)}</span>
                  </div>
                </div>
              ))}

              {invoice.invoice_type=="ADDITIONAL" && (
              <div className="space-y-2 mt-3">
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Subtotal</span>
                    <span className="font-medium text-gray-900">${fmt(invoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>TrustJet Fee ({formatPlatformFeePercent(feePercentEffective)}%)</span>
                    <span className="font-medium text-gray-900">${fmt(invoice.platform_fee)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Taxes</span>
                    <span className="font-medium text-gray-900">${fmt(invoice.tax_amount)}</span>
                  </div>
                  {parseFloat(invoice.card_fee)>0 && (
                   <div className="flex justify-between text-sm text-gray-500">
                    <span>Credit Card Fee (3%) {}</span>
                    <span className="font-medium text-gray-900">${fmt(invoice.card_fee)}</span>
                  </div>
                  )}
                  <div className="flex justify-between text-sm text-gray-900 border-t border-gray-100 pt-2">
                    <span>Total Services Cost</span>
                    <span className="font-medium">${fmt(invoice.total_amount)}</span>
                  </div>
                </div>
              )}

              {invoice.invoice_type=="ADVANCE" && (
              <div className="border-t border-gray-100 mt-1 pt-2">
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-gray-900">Total Invoice Cost</span>
                  <span className="text-sm text-gray-900 font-medium">${fmt(total)}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-gray-500">Sent on</span>
                  <span className="text-xs text-gray-500">{sentOn}</span>
                </div>
              </div>)}


            </div>
          </div>

          {/* Audit rows — Admin-only. Sits OUTSIDE the invoice card so it
              reads as page-level metadata rather than part of the invoice
              breakdown. Renders for both Advance and Additional invoice
              types. The creator/sender objects come from
              include=creator,sender on the invoice fetch above; when the id
              matches the signed-in user, reuse userProfile so the row
              reflects any in-session profile edits. */}
          {isAdminRole && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Created by</span>
                <span className="text-sm text-gray-900 font-medium">
                  {formatUserFullName(
                    invoice?.created_by === userProfile?.userprofile_id
                      ? userProfile
                      : (invoice?.creator || brokerProfile),
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Sent to Client By</span>
                <span className="text-sm text-gray-900 font-medium">
                  {formatUserFullName(
                    invoice?.sender_id === userProfile?.userprofile_id
                      ? userProfile
                      : invoice?.sender,
                  )}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-6 pt-3 border-t border-gray-100">
          {(paymentStatus=="PRE_AUTHORIZED" || paymentStatus=='WIRE_TRANSFER_IN_PROGRESS') && invoice?.status!=="PAID" && (
          <div className="absolute inset-x-0 bottom-[72px] md:bottom-0 z-10 border-t border-gray-200 bg-white/70 backdrop-blur-md px-4 py-3 space-y-2">
            <button
              type="button"
              onClick={openConfirmAndCaptureFlow}
              disabled={!!authActionLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-700 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authActionLoading === 'confirmCapture' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              <span>Confirm invoice and take payment from card</span>
            </button>

            {paymentStatus === 'WIRE_TRANSFER_IN_PROGRESS' && (
              <button
                type="button"
                onClick={handleConfirmWithoutCapture}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-700 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authActionLoading === 'confirmOnly' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                <span>Confirm invoice without taking payment from card</span>
              </button>)}

            <button
              type="button"
              onClick={handleRejectAndRelease}
              disabled={!!authActionLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 text-sm font-medium rounded-full border border-transparent hover:bg-white hover:border-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authActionLoading === 'reject' && <RiLoader4Line className="w-4 h-4 animate-spin" />}
              <span>Reject invoice and release payment from card</span>
            </button>
          </div>
          )}

          <button
            onClick={handleDownloadPDF}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-accent text-sm text-primary-700 transition-colors disabled:opacity-60"
          >
            {isGenerating ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <RiDownloadLine className="w-4 h-4" />
            )}
            {isGenerating ? 'Generating...' : 'Download PDF Invoice'}
          </button>
        </div>
      </DrawerContent>

      {/* Payment Settlement Notice — one-time informational gate before
          card capture. The component owns the checkbox state and the
          localStorage write; the drawer just runs the actual capture. */}
      <PaymentSettlementNoticeModal
        isOpen={settlementNoticeOpen}
        onClose={() => setSettlementNoticeOpen(false)}
        onConfirm={() => {
          setSettlementNoticeOpen(false);
          handleConfirmAndCapture();
        }}
        storageKey={storageKeys.INVOICE_SETTLEMENT_NOTICE_DISMISSED}
        brokerId={userProfile?.userprofile_id}
        isLoading={authActionLoading === 'confirmCapture'}
      />

      {/* Card capture error details — surfaced from the "Learn more" link
          on the toast that fires when saleCompletionMxMerchantPayment
          fails. Keeps the raw provider error out of the toast itself. */}
      <Modal
        isOpen={captureErrorOpen}
        onClose={() => setCaptureErrorOpen(false)}
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <RiErrorWarningLine className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Card payment failed</h2>
              <p className="text-sm text-gray-500">
                The processor returned the following error while trying to capture the payment:
              </p>
            </div>
          </div>
          <pre className="max-h-64 overflow-auto text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap break-words font-mono">
{captureErrorDetails || 'Unknown error'}
          </pre>
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => setCaptureErrorOpen(false)}
              className="px-4 py-2 text-sm text-white bg-primary-700 rounded-full hover:bg-primary-800"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </Drawer>
  );
};

export default AdvanceInvoiceDrawer;
